import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";

interface MallInHandLogoProps {
  /**
   * Width of the logo (height auto-calculated to maintain aspect ratio)
   * Can be number or responsive object { xs: number, sm: number, md: number, lg: number }
   */
  width?:
    | number
    | string
    | { xs?: number; sm?: number; md?: number; lg?: number };
  /**
   * Whether to show only the icon or icon + text
   */
  variant?: "icon" | "full";
  /**
   * Custom color for the logo (default: white for dark backgrounds, primary for light)
   */
  color?: "white" | "primary" | "gradient";
}

/**
 * MallInHand Premium Logo Component
 *
 * A sophisticated logo showing a hand holding a mall building with offers,
 * events, and brands emanating from it - representing mall discovery at your fingertips.
 *
 * Design Philosophy:
 * - Hand holding a stylized mall building (exploration, convenience)
 * - Offers, events, and brand icons floating out (discovery)
 * - Clean, modern aesthetic inspired by luxury lifestyle apps
 * - Premium gold accents for luxury appeal
 * - Navy foundation for trust and sophistication
 *
 * @example
 * // Full logo with text (navbar) - responsive
 * <MallInHandLogo variant="full" width={{ xs: 160, sm: 200, md: 220, lg: 240 }} color="white" />
 *
 * // Icon only (mobile, favicon)
 * <MallInHandLogo variant="icon" width={40} color="primary" />
 */
const MallInHandLogo: React.FC<MallInHandLogoProps> = ({
  width = 220,
  variant = "full",
  color = "white",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // Color mappings
  const colorMap = {
    white: {
      primary: "#FFFFFF",
      accent: "#FFD700",
      text: "#FFFFFF",
      background: "#1a237e",
    },
    primary: {
      primary: "#1a237e",
      accent: "#d4af37",
      text: "#1a237e",
      background: "#1a237e",
    },
    gradient: {
      primary: "url(#logoGradient)",
      accent: "#FFD700",
      text: "url(#textGradient)",
      background: "#1a237e",
    },
  };

  const colors = colorMap[color];

  // Handle responsive width
  let numericWidth: number;
  if (typeof width === "object" && width !== null) {
    // Responsive object
    if (isMobile && width.xs) {
      numericWidth = width.xs;
    } else if (isTablet && width.sm) {
      numericWidth = width.sm;
    } else if (isDesktop && width.md) {
      numericWidth = width.md;
    } else if (width.lg) {
      numericWidth = width.lg;
    } else {
      numericWidth = 220; // fallback
    }
  } else {
    numericWidth =
      typeof width === "number" ? width : parseInt(width as string);
  }

  const iconWidth = variant === "icon" ? numericWidth : numericWidth * 0.25;
  const logoWidth = numericWidth;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: variant === "full" ? { xs: 1, sm: 1.5, md: 2 } : 0,
        userSelect: "none",
        cursor: "pointer",
        transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        transformOrigin: "center",
        willChange: "transform",
        backfaceVisibility: "hidden",
        WebkitFontSmoothing: "antialiased",
        "&:hover": {
          transform: "scale(1.05)",
        },
      }}
    >
      {/* SVG Icon - Clear Hand Holding Mall with Discovery Elements */}
      <svg
        width={iconWidth}
        height={iconWidth}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1a237e" />
            <stop offset="100%" stopColor="#3949ab" />
          </linearGradient>
          <linearGradient
            id="accentGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#FFD700" />
            <stop offset="100%" stopColor="#FFA000" />
          </linearGradient>
        </defs>

        {/* HAND - Gold filled with black outline */}
        {/* Palm base - hand-shaped base with wrist */}
        <path
          d="M 22 75 Q 22 82 28 85 L 72 85 Q 78 82 78 75 L 78 65 Q 78 58 72 58 L 68 58 L 60 60 L 52 60 L 44 60 L 36 58 L 28 58 Q 22 58 22 65 Z"
          fill={colors.accent}
          stroke="#000000"
          strokeWidth="2.5"
        />

        {/* Fingers - connected properly to palm */}
        {/* Thumb - left side */}
        <ellipse
          cx="25"
          cy="60"
          rx="6"
          ry="15"
          fill={colors.accent}
          stroke="#000000"
          strokeWidth="2.5"
          transform="rotate(-25 25 60)"
        />

        {/* Index finger (leftmost vertical) */}
        <rect
          x="32"
          y="35"
          width="8"
          height="40"
          rx="4"
          fill={colors.accent}
          stroke="#000000"
          strokeWidth="2.5"
        />

        {/* Middle finger (tallest) */}
        <rect
          x="42"
          y="28"
          width="8"
          height="47"
          rx="4"
          fill={colors.accent}
          stroke="#000000"
          strokeWidth="2.5"
        />

        {/* Ring finger */}
        <rect
          x="52"
          y="32"
          width="8"
          height="43"
          rx="4"
          fill={colors.accent}
          stroke="#000000"
          strokeWidth="2.5"
        />

        {/* Pinky (rightmost) */}
        <rect
          x="62"
          y="40"
          width="8"
          height="35"
          rx="4"
          fill={colors.accent}
          stroke="#000000"
          strokeWidth="2.5"
        />

        {/* MALL BUILDING - On the palm, gold filled with black outline */}
        <rect
          x="38"
          y="50"
          width="26"
          height="20"
          rx="2"
          fill={colors.accent}
          stroke="#000000"
          strokeWidth="2.5"
        />

        {/* Mall windows - black filled */}
        <rect x="41" y="54" width="5" height="5" rx="0.5" fill="#000000" />
        <rect x="48" y="54" width="5" height="5" rx="0.5" fill="#000000" />
        <rect x="55" y="54" width="5" height="5" rx="0.5" fill="#000000" />
        <rect x="41" y="61" width="5" height="5" rx="0.5" fill="#000000" />
        <rect x="48" y="61" width="5" height="5" rx="0.5" fill="#000000" />
        <rect x="55" y="61" width="5" height="5" rx="0.5" fill="#000000" />

        {/* Mall entrance door */}
        {/* <rect
          x="46"
          y="65"
          width="10"
          height="5"
          rx="0.5"
          fill={colors.primary}
          stroke="#000000"
        /> */}

        {/* Mall roof */}
        <path
          d="M 36 50 L 51 45 L 66 50"
          stroke="#000000"
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
        />

        {/* FLOATING ELEMENTS - All gold filled with black outlines */}

        {/* Parking Ticket - Top left */}
        <g transform="translate(5, 10)">
          <rect
            x="0"
            y="0"
            width="18"
            height="24"
            rx="2"
            fill={colors.accent}
            stroke="#000000"
            strokeWidth="2"
          />
          {/* Ticket notch at top */}
          <path d="M 6 0 L 6 4 L 12 4 L 12 0" fill="#000000" />
          {/* Barcode lines */}
          <line
            x1="3"
            y1="8"
            x2="15"
            y2="8"
            stroke="#000000"
            strokeWidth="1.5"
          />
          <line
            x1="4"
            y1="11"
            x2="14"
            y2="11"
            stroke="#000000"
            strokeWidth="1"
          />
          <line
            x1="5"
            y1="14"
            x2="13"
            y2="14"
            stroke="#000000"
            strokeWidth="1"
          />
          {/* <line
            x1="4"
            y1="17"
            x2="14"
            y2="17"
            stroke="#000000"
            strokeWidth="1"
          /> */}
          {/* P symbol for parking */}
          <text
            x="9"
            y="22"
            fontSize="8"
            fontWeight="bold"
            fill="#000000"
            textAnchor="middle"
          >
            P
          </text>
        </g>

        {/* Offer Tag - Left side, near thumb */}
        <g transform="translate(-5, 52)">
          <path
            d="M 5 15 L 18 0 L 31 15 L 18 30 Z"
            fill={colors.accent}
            stroke="#000000"
            strokeWidth="2.5"
          />
          <text
            x="18"
            y="21"
            fontSize="16"
            fontWeight="bold"
            fill="#000000"
            textAnchor="middle"
          >
            %
          </text>
        </g>

        {/* Event Calendar - Top right */}
        <g transform="translate(74, 8)">
          <rect
            x="0"
            y="6"
            width="20"
            height="18"
            rx="2"
            fill={colors.accent}
            stroke="#000000"
            strokeWidth="2"
          />
          {/* Calendar rings */}
          <rect
            x="3"
            y="0"
            width="3"
            height="8"
            rx="1.5"
            fill={colors.accent}
            stroke="#000000"
            strokeWidth="1.5"
          />
          <rect
            x="14"
            y="0"
            width="3"
            height="8"
            rx="1.5"
            fill={colors.accent}
            stroke="#000000"
            strokeWidth="1.5"
          />
          {/* Date lines */}
          <line
            x1="3"
            y1="12"
            x2="17"
            y2="12"
            stroke="#000000"
            strokeWidth="1.5"
          />
          <line
            x1="3"
            y1="16"
            x2="17"
            y2="16"
            stroke="#000000"
            strokeWidth="1.5"
          />
          <line
            x1="3"
            y1="20"
            x2="17"
            y2="20"
            stroke="#000000"
            strokeWidth="1.5"
          />
        </g>

        {/* Brand/Store Icon - Right side */}
        <g transform="translate(76, 50)">
          <rect
            x="0"
            y="8"
            width="18"
            height="14"
            rx="1"
            fill={colors.accent}
            stroke="#000000"
            strokeWidth="2"
          />
          {/* Store awning */}
          <path
            d="M -2 8 L 9 3 L 20 8"
            fill={colors.accent}
            stroke="#000000"
            strokeWidth="2"
          />
          {/* Door */}
          <rect
            x="6"
            y="14"
            width="6"
            height="8"
            fill="#FFFFFF"
            stroke="#000000"
            strokeWidth="2"
          />
        </g>

        {/* Discovery sparkles - gold */}
        <circle cx="12" cy="38" r="2.5" fill={colors.accent} />
        <circle cx="84" cy="35" r="2.5" fill={colors.accent} />
        <circle cx="8" cy="80" r="2.5" fill={colors.accent} />
        <circle cx="84" cy="77" r="2.5" fill={colors.accent} />
      </svg>

      {/* Text Logo */}
      {variant === "full" && (
        <svg
          width={logoWidth * 0.8}
          height={iconWidth * 1.3}
          viewBox="0 0 280 70"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="textGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#1a237e" />
              <stop offset="50%" stopColor="#3949ab" />
              <stop offset="100%" stopColor="#5e35b1" />
            </linearGradient>
          </defs>

          {/* MallInHand Text - Extra Large */}
          <text
            x="0"
            y="42"
            fontFamily="'Poppins', 'Roboto', 'Helvetica', sans-serif"
            fontSize="42"
            fontWeight="700"
            letterSpacing="-0.8"
            fill={colors.text}
          >
            Mall
            <tspan fill={colors.accent}>In</tspan>
            Hand
          </text>

          {/* Tagline - Extra Large */}
          <text
            x="2"
            y="60"
            fontFamily="'Roboto', 'Helvetica', sans-serif"
            fontSize="12"
            fontWeight="600"
            letterSpacing="2"
            fill={colors.text}
            opacity="0.85"
          >
            EXPLORE MALL, DIGITALLY
          </text>
        </svg>
      )}
    </Box>
  );
};

export default MallInHandLogo;
